import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import PageHeader from "../../components/controls/PageHeader";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";

// Components
import FeaturedCarousel from "../../components/news/FeaturedCarousel";
import NewsTags from "../../components/news/NewsTags";
import NewsFilters from "../../components/news/NewsFilters";
import NewsGrid from "../../components/news/NewsGrid";
import NewsPagination from "../../components/news/NewsPagination";

export default function NewsPage() {
  const { t } = useTranslation();

  /* ------------------------------ */
  /* STATE */
  /* ------------------------------ */

  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [sort, setSort] = useState("recent");

  const [featured, setFeatured] = useState([]);
  const [news, setNews] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const pageSize = 9;

  /* ------------------------------ */
  /* LOAD TAGS */
  /* ------------------------------ */

  const loadTags = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/News/tags`
      );
      const data = await res.json();

      // Tag format : { id, nameFr, nameEn }
      setTags(data);
    } catch (e) {
      console.error("Erreur loadTags:", e);
    }
  };

  /* ------------------------------ */
  /* LOAD FEATURED */
  /* ------------------------------ */
  const loadFeatured = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/News/featured`
      );
      const data = await res.json();

      setFeatured(
        data.map((item) => ({
          id: item.id,
          title: item.title,
          image: item.imageUrl,
          slug: item.slug,
        }))
      );
    } catch (e) {
      console.error("Erreur loadFeatured:", e);
    }
  };

  /* ------------------------------ */
  /* LOAD NEWS PUBLIC */
  /* ------------------------------ */
  const loadNews = async () => {
    try {
      const tagId = selectedTags.length === 1 ? selectedTags[0] : "";

      const url =
        `${process.env.REACT_APP_API_BASE_URL}/api/News/public?` +
        `page=${currentPage}&size=${pageSize}` +
        (tagId ? `&tagId=${tagId}` : "") +
        `&sort=${sort}`;

      const res = await fetch(url);
      const data = await res.json();

      setNews(
        (data.items || []).map((n) => ({
          id: n.id,
          title: n.title,
          image: n.imageUrl,
          slug: n.slug,
          content: n.content,
          date: n.publishDate,
          tags: n.tagId ? [n.tagId] : [],
        }))
      );

      setTotalPages(Math.ceil((data.total || 1) / pageSize));
    } catch (e) {
      console.error("Erreur loadNews:", e);
    }
  };

  /* ------------------------------ */
  /* EFFECTS */
  /* ------------------------------ */
  useEffect(() => {
    loadTags();
    loadFeatured();
  }, []);

  useEffect(() => {
    loadNews();
  }, [selectedTags, sort, currentPage]);

  /* ------------------------------ */
  /* TOGGLE TAG */
  /* ------------------------------ */
  const toggleTag = (tagId) => {
    setCurrentPage(1);

    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((x) => x !== tagId) : [...prev, tagId]
    );
  };

  /* ------------------------------ */
  /* RENDER */
  /* ------------------------------ */

  return (
    <div className="min-h-screen bg-white">
      <PageHeader
        title="Actualités"
        subtitle="Découvrez les dernières nouvelles des entreprises du Québec"
        logoSrc={AnanasSolutionsLogo}
        align="center"
        fullBleed={true}
        rounded={false}
        height="sm"
        gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
        patternSize="32px 32px"
        patternOpacity={15}
      />

      <div className="max-w-6xl mx-auto px-4 pt-10 pb-20">
        {/* FEATURED */}
        <FeaturedCarousel items={featured} />

        {/* TAGS */}
        <NewsTags
          tags={tags}
          selectedTags={selectedTags}
          toggleTag={toggleTag}
        />

        {/* FILTERS */}
        <div className="mt-4 flex justify-end hidden">
          <NewsFilters sort={sort} setSort={setSort} />
        </div>

        {/* GRID */}
        <NewsGrid items={news} />

        {/* PAGINATION */}
        <NewsPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
